import React, {
    useEffect,
    useState,
} from "react";

import axios from "axios";

import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaCheck,
    FaTimes,
    FaImage,
    FaVideo,
    FaStar,
    FaSearch,
    FaSpinner,
} from "react-icons/fa";

import "./GalleryManagement.css";

const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000";

const token = localStorage.getItem("token");

const emptyItem = () => ({
    title: "",
    location: "",
    category: "Safari",
    type: "image",
    video_url: "",
    featured: false,
    status: "active",
    sort_order: 0,
});

function GalleryManagement() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    /* =====================================================
       CREATE MODE — array of { file, preview, data }
       EDIT MODE  — form holds the single item's data
    ===================================================== */
    const [form, setForm] = useState(emptyItem());

    // Each entry: { id, file, preview, data }
    const [drafts, setDrafts] = useState([]);

    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    /* =====================================================
       FETCH GALLERY
    ===================================================== */
    const fetchGallery = async () => {
        try {
            setLoading(true);

            const response = await axios.get(
                `${API_BASE_URL}/api/gallery/admin/all`,
                {
                    params: {
                        limit: 100,
                        search,
                        status: statusFilter || undefined,
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setItems(response.data.data || []);
        } catch (error) {
            console.error("Failed to load gallery:", error);
            alert("Failed to load gallery items");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchGallery();
        }, 300);

        return () => clearTimeout(timer);
    }, [search, statusFilter]);

    /* =====================================================
       MODAL OPEN/CLOSE
    ===================================================== */
    const openCreateModal = () => {
        setEditingItem(null);
        setForm(emptyItem());
        clearDrafts();
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditingItem(item);

        setForm({
            title: item.title || "",
            location: item.location || "",
            category: item.category || "General",
            type: item.type || "image",
            video_url: item.video_url || "",
            featured: Boolean(item.featured),
            status: item.status || "active",
            sort_order: item.sort_order || 0,
        });

        clearDrafts();

        // Show existing media as a single preview
        const existingUrl =
            item.type === "image"
                ? item.image_url
                : item.thumbnail_url;

        if (existingUrl) {
            setDrafts([
                {
                    id: `existing-${item.id}`,
                    file: null,
                    preview: existingUrl,
                    data: null, // not editable, just display
                },
            ]);
        }

        setShowModal(true);
    };

    const closeModal = () => {
        clearDrafts();
        setShowModal(false);
        setEditingItem(null);
        setForm(emptyItem());
    };

    const clearDrafts = () => {
        setDrafts((prev) => {
            prev.forEach((d) => {
                if (d.preview && d.preview.startsWith("blob:")) {
                    URL.revokeObjectURL(d.preview);
                }
            });
            return [];
        });
    };

    /* =====================================================
       SHARED FORM CHANGE (edit mode + shared defaults)
    ===================================================== */
    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    /* =====================================================
       DRAFTS (create mode)

       Each draft:
       {
         id: unique,
         file: File,
         preview: blob:,
         data: {
           title, location, category, type,
           video_url, featured, status, sort_order
         }
       }
    ===================================================== */

    const handleFilesChange = (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;

        // Start with current form values as defaults for each new draft
        const defaults = { ...form };

        const newDrafts = files.map((file) => {
            const baseName =
                file.name.replace(/\.[^/.]+$/, ""); // strip extension

            return {
                id: `${Date.now()}-${Math.random()
                    .toString(36)
                    .slice(2)}`,
                file,
                preview: URL.createObjectURL(file),
                data: {
                    ...defaults,
                    title: baseName, // prefill with file name
                },
            };
        });

        setDrafts((prev) => [...prev, ...newDrafts]);

        // Reset the file input so picking the same file again works
        event.target.value = "";
    };

    const updateDraft = (id, field, value) => {
        setDrafts((prev) =>
            prev.map((d) =>
                d.id === id
                    ? {
                        ...d,
                        data: {
                            ...d.data,
                            [field]: value,
                        },
                    }
                    : d
            )
        );
    };

    const updateDraftCheckbox = (id, field, checked) => {
        setDrafts((prev) =>
            prev.map((d) =>
                d.id === id
                    ? {
                        ...d,
                        data: {
                            ...d.data,
                            [field]: checked,
                        },
                    }
                    : d
            )
        );
    };

    const removeDraft = (id) => {
        setDrafts((prev) => {
            const target = prev.find((d) => d.id === id);

            if (target?.preview?.startsWith("blob:")) {
                URL.revokeObjectURL(target.preview);
            }

            return prev.filter((d) => d.id !== id);
        });
    };

    /* =====================================================
       SUBMIT
    ===================================================== */
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (uploading) return;

        /* ==========================================
           EDIT MODE — single PUT
        ========================================== */
        if (editingItem) {
            setUploading(true);

            try {
                const formData = new FormData();

                Object.entries(form).forEach(([key, value]) => {
                    formData.append(key, value);
                });

                // Only include a file if the user picked a new one
                const firstDraft = drafts[0];
                if (firstDraft?.file) {
                    formData.append("image", firstDraft.file);
                }

                await axios.put(
                    `${API_BASE_URL}/api/gallery/${editingItem.id}`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                closeModal();
                await fetchGallery();
            } catch (error) {
                console.error(error);
                alert(
                    error.response?.data?.message ||
                    "Something went wrong"
                );
            } finally {
                setUploading(false);
            }

            return;
        }

        /* ==========================================
           CREATE MODE — one POST per draft
        ========================================== */
        if (!drafts.length) {
            alert("Please select at least one file.");
            return;
        }

        // Validate each draft
        for (let i = 0; i < drafts.length; i++) {
            const d = drafts[i];

            if (!d.data.title?.trim()) {
                alert(`Title is required for item ${i + 1}.`);
                return;
            }

            if (
                d.data.type === "video" &&
                !d.data.video_url?.trim()
            ) {
                alert(
                    `Video URL is required for item ${i + 1}.`
                );
                return;
            }
        }

        setUploading(true);
        setProgress({ done: 0, total: drafts.length });

        try {
            let done = 0;
            const failures = [];

            for (const draft of drafts) {
                try {
                    const formData = new FormData();

                    Object.entries(draft.data).forEach(
                        ([key, value]) => {
                            formData.append(key, value);
                        }
                    );

                    formData.append("image", draft.file);

                    await axios.post(
                        `${API_BASE_URL}/api/gallery`,
                        formData,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );

                    done++;
                    setProgress({
                        done,
                        total: drafts.length,
                    });
                } catch (error) {
                    failures.push({
                        title: draft.data.title,
                        message:
                            error.response?.data?.message ||
                            "Upload failed",
                    });
                }
            }

            closeModal();
            await fetchGallery();

            if (failures.length === 0) {
                alert(
                    `${done} item${done > 1 ? "s" : ""} uploaded successfully.`
                );
            } else if (done === 0) {
                alert(
                    `All uploads failed.\n\n${failures
                        .map((f) => `• ${f.title}: ${f.message}`)
                        .join("\n")}`
                );
            } else {
                alert(
                    `${done} uploaded, ${failures.length} failed.\n\n${failures
                        .map((f) => `• ${f.title}: ${f.message}`)
                        .join("\n")}`
                );
            }
        } finally {
            setUploading(false);
            setProgress({ done: 0, total: 0 });
        }
    };

    /* =====================================================
       ROW ACTIONS
    ===================================================== */
    const toggleStatus = async (item) => {
        const newStatus =
            item.status === "active" ? "inactive" : "active";

        try {
            await axios.patch(
                `${API_BASE_URL}/api/gallery/${item.id}/status`,
                { status: newStatus },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            fetchGallery();
        } catch (error) {
            console.error(error);
            alert("Failed to update status");
        }
    };

    const toggleFeatured = async (item) => {
        try {
            await axios.patch(
                `${API_BASE_URL}/api/gallery/${item.id}/featured`,
                { featured: !Boolean(item.featured) },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            fetchGallery();
        } catch (error) {
            console.error(error);
            alert("Failed to update featured status");
        }
    };

    const deleteItem = async (item) => {
        const confirmed = window.confirm(
            `Delete "${item.title}" permanently?`
        );

        if (!confirmed) return;

        try {
            await axios.delete(
                `${API_BASE_URL}/api/gallery/${item.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            fetchGallery();
        } catch (error) {
            console.error(error);
            alert("Failed to delete gallery item");
        }
    };

    /* =====================================================
       MEDIA URL HELPER
    ===================================================== */
    const getMediaUrl = (mediaUrl) => {
        if (!mediaUrl) return "";

        if (
            mediaUrl.startsWith("http") ||
            mediaUrl.startsWith("blob:") ||
            mediaUrl.startsWith("data:")
        ) {
            return mediaUrl;
        }

        return `${API_BASE_URL}${mediaUrl}`;
    };

    /* =====================================================
       RENDER
    ===================================================== */
    return (
        <div className="gallery-management">
            {/* HEADER */}
            <div className="gm-header">
                <div>
                    <span>MEDIA MANAGEMENT</span>
                    <h1>
                        Gallery
                        <strong> Management</strong>
                    </h1>
                    <p>
                        Manage your travel photos,
                        videos and experiences.
                    </p>
                </div>

                <button
                    className="gm-add-button"
                    onClick={openCreateModal}
                    disabled={uploading}
                >
                    <FaPlus />
                    Add Media
                </button>
            </div>

            {/* CONTROLS */}
            <div className="gm-controls">
                <div className="gm-search">
                    <FaSearch />
                    <input
                        placeholder="Search gallery..."
                        value={search}
                        onChange={(event) =>
                            setSearch(event.target.value)
                        }
                    />
                </div>

                <select
                    value={statusFilter}
                    onChange={(event) =>
                        setStatusFilter(event.target.value)
                    }
                >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            </div>

            {/* GRID */}
            {loading ? (
                <div className="gm-loading">
                    Loading gallery...
                </div>
            ) : (
                <div className="gm-grid">
                    {items.map((item) => {
                        const media =
                            item.type === "image"
                                ? item.image_url
                                : item.thumbnail_url;

                        return (
                            <div
                                className={`gm-card ${item.status === "inactive"
                                        ? "inactive"
                                        : ""
                                    }`}
                                key={item.id}
                            >
                                <div className="gm-image">
                                    {media ? (
                                        <img
                                            src={getMediaUrl(media)}
                                            alt={item.title}
                                        />
                                    ) : (
                                        <div className="gm-no-image">
                                            <FaVideo />
                                        </div>
                                    )}

                                    <div className="gm-badges">
                                        <span>
                                            {item.type === "video" ? (
                                                <FaVideo />
                                            ) : (
                                                <FaImage />
                                            )}
                                            {item.type}
                                        </span>

                                        {item.featured === 1 && (
                                            <span className="featured">
                                                <FaStar />
                                                Featured
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="gm-content">
                                    <h3>{item.title}</h3>
                                    <p>
                                        {item.location ||
                                            "No location"}
                                    </p>
                                    <small>{item.category}</small>

                                    <div className="gm-status">
                                        <span className={item.status}>
                                            {item.status === "active" ? (
                                                <FaCheck />
                                            ) : (
                                                <FaTimes />
                                            )}
                                            {item.status}
                                        </span>
                                    </div>

                                    <div className="gm-actions">
                                        <button
                                            onClick={() =>
                                                openEditModal(item)
                                            }
                                            title="Edit"
                                        >
                                            <FaEdit />
                                        </button>

                                        <button
                                            onClick={() =>
                                                toggleFeatured(item)
                                            }
                                            className={
                                                item.featured === 1
                                                    ? "active-star"
                                                    : ""
                                            }
                                            title="Featured"
                                        >
                                            <FaStar />
                                        </button>

                                        <button
                                            onClick={() =>
                                                toggleStatus(item)
                                            }
                                            title={
                                                item.status === "active"
                                                    ? "Deactivate"
                                                    : "Activate"
                                            }
                                        >
                                            {item.status === "active" ? (
                                                <FaTimes />
                                            ) : (
                                                <FaCheck />
                                            )}
                                        </button>

                                        <button
                                            className="delete"
                                            onClick={() =>
                                                deleteItem(item)
                                            }
                                            title="Delete"
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* MODAL */}
            {showModal && (
                <div className="gm-modal" onClick={closeModal}>
                    <div
                        className="gm-modal-content gm-modal-large"
                        onClick={(event) =>
                            event.stopPropagation()
                        }
                    >
                        <button
                            className="gm-close"
                            onClick={closeModal}
                            disabled={uploading}
                        >
                            <FaTimes />
                        </button>

                        <h2>
                            {editingItem
                                ? "Edit Gallery Item"
                                : `Add Gallery Item${drafts.length > 1
                                    ? `s (${drafts.length})`
                                    : ""
                                }`}
                        </h2>

                        <form onSubmit={handleSubmit}>
                            {/* =========================================
                                EDIT MODE — single form
                                CREATE MODE — one card per draft
                            ========================================= */}

                            {editingItem ? (
                                <EditForm
                                    form={form}
                                    handleChange={handleChange}
                                    onFilesChange={handleFilesChange}
                                    drafts={drafts}
                                    onRemoveDraft={removeDraft}
                                    getMediaUrl={getMediaUrl}
                                    uploading={uploading}
                                    setForm={setForm}
                                />
                            ) : (
                                <CreateDrafts
                                    drafts={drafts}
                                    onFilesChange={handleFilesChange}
                                    updateDraft={updateDraft}
                                    updateDraftCheckbox={
                                        updateDraftCheckbox
                                    }
                                    removeDraft={removeDraft}
                                    getMediaUrl={getMediaUrl}
                                    uploading={uploading}
                                />
                            )}

                            <div className="gm-modal-actions">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="cancel"
                                    disabled={uploading}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="save"
                                    disabled={uploading}
                                >
                                    {uploading ? (
                                        <>
                                            <FaSpinner className="gm-spin" />
                                            {progress.total > 1
                                                ? `Uploading ${progress.done} of ${progress.total}...`
                                                : "Uploading..."}
                                        </>
                                    ) : editingItem ? (
                                        "Update Item"
                                    ) : (
                                        `Upload ${drafts.length > 1
                                            ? `${drafts.length} Items`
                                            : "Item"
                                        }`
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

/* =====================================================
   EDIT FORM — one item
===================================================== */
function EditForm({
    form,
    handleChange,
    onFilesChange,
    drafts,
    onRemoveDraft,
    getMediaUrl,
    uploading,
    setForm,
}) {
    return (
        <>
            <div className="gm-form-grid">
                <div className="gm-form-group full">
                    <label>Title *</label>
                    <input
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        required
                        disabled={uploading}
                    />
                </div>

                <div className="gm-form-group">
                    <label>Media Type</label>
                    <select
                        name="type"
                        value={form.type}
                        onChange={handleChange}
                        disabled={uploading}
                    >
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                    </select>
                </div>

                <div className="gm-form-group">
                    <label>Category</label>
                    <input
                        name="category"
                        value={form.category}
                        onChange={handleChange}
                        disabled={uploading}
                    />
                </div>

                <div className="gm-form-group">
                    <label>Location</label>
                    <input
                        name="location"
                        value={form.location}
                        onChange={handleChange}
                        disabled={uploading}
                    />
                </div>

                <div className="gm-form-group">
                    <label>Sort Order</label>
                    <input
                        type="number"
                        name="sort_order"
                        value={form.sort_order}
                        onChange={handleChange}
                        disabled={uploading}
                    />
                </div>

                {form.type === "video" && (
                    <div className="gm-form-group full">
                        <label>Video URL *</label>
                        <input
                            type="url"
                            name="video_url"
                            value={form.video_url}
                            onChange={handleChange}
                            required
                            disabled={uploading}
                        />
                    </div>
                )}

                <div className="gm-form-group full">
                    <label>
                        {form.type === "video"
                            ? "Video Thumbnail"
                            : "Replace Image (optional)"}
                    </label>

                    <input
                        type="file"
                        accept="image/*"
                        onChange={onFilesChange}
                        disabled={uploading}
                    />
                </div>

                {drafts.length > 0 && (
                    <div className="gm-preview-grid full">
                        {drafts.map((p) => (
                            <div
                                className="gm-preview-item"
                                key={p.id}
                            >
                                <img
                                    src={getMediaUrl(p.preview)}
                                    alt="Preview"
                                />

                                {p.file && (
                                    <button
                                        type="button"
                                        className="gm-remove-preview"
                                        onClick={() =>
                                            onRemoveDraft(p.id)
                                        }
                                        disabled={uploading}
                                        title="Remove"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="gm-checkboxes">
                <label>
                    <input
                        type="checkbox"
                        name="featured"
                        checked={form.featured}
                        onChange={handleChange}
                        disabled={uploading}
                    />
                    Featured
                </label>

                <label>
                    <input
                        type="checkbox"
                        checked={form.status === "active"}
                        onChange={(event) =>
                            setForm((previous) => ({
                                ...previous,
                                status: event.target.checked
                                    ? "active"
                                    : "inactive",
                            }))
                        }
                        disabled={uploading}
                    />
                    Active
                </label>
            </div>
        </>
    );
}

/* =====================================================
   CREATE DRAFTS — one card per file
===================================================== */
function CreateDrafts({
    drafts,
    onFilesChange,
    updateDraft,
    updateDraftCheckbox,
    removeDraft,
    getMediaUrl,
    uploading,
}) {
    return (
        <>
            {/* =========================================
                FILE PICKER
                You can add more files at any time.
            ========================================= */}
            <div className="gm-drafts-toolbar">
                <label className="gm-add-files">
                    <FaPlus />
                    Choose Files
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={onFilesChange}
                        hidden
                        disabled={uploading}
                    />
                </label>

                <span className="gm-drafts-count">
                    {drafts.length === 0
                        ? "No files selected yet"
                        : `${drafts.length} file${drafts.length > 1 ? "s" : ""
                        } ready`}
                </span>
            </div>

            {/* =========================================
                DRAFTS LIST
            ========================================= */}
            {drafts.length > 0 && (
                <div className="gm-drafts-list">
                    {drafts.map((draft, index) => (
                        <div
                            className="gm-draft-card"
                            key={draft.id}
                        >
                            {/* LEFT: PREVIEW */}
                            <div className="gm-draft-preview">
                                <img
                                    src={getMediaUrl(draft.preview)}
                                    alt={draft.data.title}
                                />

                                <button
                                    type="button"
                                    className="gm-remove-preview"
                                    onClick={() =>
                                        removeDraft(draft.id)
                                    }
                                    disabled={uploading}
                                    title="Remove"
                                >
                                    <FaTimes />
                                </button>

                                <span className="gm-draft-index">
                                    {index + 1}
                                </span>
                            </div>

                            {/* RIGHT: FORM */}
                            <div className="gm-draft-form">
                                <div className="gm-form-grid">
                                    <div className="gm-form-group full">
                                        <label>Title *</label>
                                        <input
                                            type="text"
                                            value={
                                                draft.data.title
                                            }
                                            onChange={(e) =>
                                                updateDraft(
                                                    draft.id,
                                                    "title",
                                                    e.target.value
                                                )
                                            }
                                            disabled={uploading}
                                        />
                                    </div>

                                    <div className="gm-form-group">
                                        <label>
                                            Media Type
                                        </label>
                                        <select
                                            value={
                                                draft.data.type
                                            }
                                            onChange={(e) =>
                                                updateDraft(
                                                    draft.id,
                                                    "type",
                                                    e.target.value
                                                )
                                            }
                                            disabled={uploading}
                                        >
                                            <option value="image">
                                                Image
                                            </option>
                                            <option value="video">
                                                Video
                                            </option>
                                        </select>
                                    </div>

                                    <div className="gm-form-group">
                                        <label>Category</label>
                                        <input
                                            type="text"
                                            value={
                                                draft.data
                                                    .category
                                            }
                                            onChange={(e) =>
                                                updateDraft(
                                                    draft.id,
                                                    "category",
                                                    e.target.value
                                                )
                                            }
                                            disabled={uploading}
                                        />
                                    </div>

                                    <div className="gm-form-group">
                                        <label>Location</label>
                                        <input
                                            type="text"
                                            value={
                                                draft.data
                                                    .location
                                            }
                                            onChange={(e) =>
                                                updateDraft(
                                                    draft.id,
                                                    "location",
                                                    e.target.value
                                                )
                                            }
                                            disabled={uploading}
                                        />
                                    </div>

                                    <div className="gm-form-group">
                                        <label>Sort Order</label>
                                        <input
                                            type="number"
                                            value={
                                                draft.data
                                                    .sort_order
                                            }
                                            onChange={(e) =>
                                                updateDraft(
                                                    draft.id,
                                                    "sort_order",
                                                    e.target.value
                                                )
                                            }
                                            disabled={uploading}
                                        />
                                    </div>

                                    {draft.data.type ===
                                        "video" && (
                                            <div className="gm-form-group full">
                                                <label>
                                                    Video URL *
                                                </label>
                                                <input
                                                    type="url"
                                                    value={
                                                        draft.data
                                                            .video_url
                                                    }
                                                    onChange={(e) =>
                                                        updateDraft(
                                                            draft.id,
                                                            "video_url",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={
                                                        uploading
                                                    }
                                                    required
                                                />
                                            </div>
                                        )}
                                </div>

                                <div className="gm-checkboxes">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={
                                                draft.data
                                                    .featured
                                            }
                                            onChange={(e) =>
                                                updateDraftCheckbox(
                                                    draft.id,
                                                    "featured",
                                                    e.target.checked
                                                )
                                            }
                                            disabled={uploading}
                                        />
                                        Featured
                                    </label>

                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={
                                                draft.data
                                                    .status ===
                                                "active"
                                            }
                                            onChange={(e) =>
                                                updateDraft(
                                                    draft.id,
                                                    "status",
                                                    e.target.checked
                                                        ? "active"
                                                        : "inactive"
                                                )
                                            }
                                            disabled={uploading}
                                        />
                                        Active
                                    </label>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

export default GalleryManagement;